import React from 'react';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  icon?: string;
  disabled?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Onayla',
  cancelText = 'İptal',
  type = 'danger',
  icon,
  disabled = false
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          bgColor: 'bg-red-100 dark:bg-red-900/30',
          iconColor: 'text-red-600 dark:text-red-400',
          buttonColor: disabled ? 'bg-red-400 dark:bg-red-800 cursor-not-allowed text-white' : 'bg-red-600 hover:bg-red-700 text-white',
          defaultIcon: 'fas fa-exclamation-triangle'
        };
      case 'warning':
        return {
          bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
          iconColor: 'text-yellow-600 dark:text-yellow-400',
          buttonColor: disabled ? 'bg-yellow-400 dark:bg-yellow-800 cursor-not-allowed text-white' : 'bg-yellow-600 hover:bg-yellow-700 text-white',
          defaultIcon: 'fas fa-exclamation-circle'
        };
      case 'info':
        return {
          bgColor: 'bg-blue-100 dark:bg-blue-900/30',
          iconColor: 'text-blue-600 dark:text-blue-400',
          buttonColor: disabled ? 'bg-blue-400 dark:bg-blue-800 cursor-not-allowed text-white' : 'bg-blue-600 hover:bg-blue-700 text-white',
          defaultIcon: 'fas fa-info-circle'
        };
      case 'success':
        return {
          bgColor: 'bg-green-100 dark:bg-green-900/30',
          iconColor: 'text-green-600 dark:text-green-400',
          buttonColor: disabled ? 'bg-green-400 dark:bg-green-800 cursor-not-allowed text-white' : 'bg-green-600 hover:bg-green-700 text-white',
          defaultIcon: 'fas fa-check-circle'
        };
      default:
        return {
          bgColor: 'bg-gray-100 dark:bg-gray-700',
          iconColor: 'text-gray-600 dark:text-gray-400',
          buttonColor: disabled ? 'bg-gray-400 dark:bg-gray-800 cursor-not-allowed text-white' : 'bg-gray-600 hover:bg-gray-700 text-white',
          defaultIcon: 'fas fa-question-circle'
        };
    }
  };

  const styles = getTypeStyles();
  const displayIcon = icon || styles.defaultIcon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 transition-colors duration-300">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className={`${styles.bgColor} rounded-lg p-3 mr-4`}>
              <i className={`${displayIcon} ${styles.iconColor} text-xl`}></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">{title}</h3>              
            </div>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-700 dark:text-gray-300 transition-colors leading-relaxed">
              {message}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button 
              onClick={onClose}
              disabled={disabled}
              className={`px-4 py-3 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-base ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {cancelText}
            </button>
            <button 
              onClick={() => {
                if (!disabled) {
                  onConfirm();
                }
              }}
              disabled={disabled}
              className={`px-4 py-3 sm:py-2 rounded-lg transition-colors text-base ${styles.buttonColor}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
